import { useState } from "react";
import { Form, FormikProvider, useFormik } from "formik";
import { Button } from "@design-system/Button/Button";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";
import { validateFormikWithZodSchema } from "@helpers/form";
import {
    formFieldsPlaygroundInitialTouched,
    formFieldsPlaygroundInitialValues,
    formFieldsPlaygroundSchema,
    type FormFieldsPlaygroundValues,
} from "./formFieldsPlayground.schema";
import { FormFieldsPlaygroundChoiceRows } from "./FormFieldsPlaygroundChoiceRows";
import { FormFieldsPlaygroundContactRows } from "./FormFieldsPlaygroundContactRows";
import { FormFieldsPlaygroundLabellessRows } from "./FormFieldsPlaygroundLabellessRows";
import { FormFieldsPlaygroundPickerRows } from "./FormFieldsPlaygroundPickerRows";
import { FormFieldsPlaygroundRows } from "./FormFieldsPlaygroundRows";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";

type JsonValue = string | number | boolean | object | null | undefined;

const showUndefined = (_key: string, value: JsonValue) =>
    value === undefined ? "(undefined)" : value;

/**
 * A real `useFormik` form built from the typed field kit, validated by a Zod
 * schema. Submit shows the values the kit wrote, including cleared fields.
 */
export const FormFieldsPlayground = () => {
    const [submittedValues, setSubmittedValues] =
        useState<FormFieldsPlaygroundValues>();
    const formik = useFormik<FormFieldsPlaygroundValues>({
        initialValues: formFieldsPlaygroundInitialValues,
        initialTouched: formFieldsPlaygroundInitialTouched,
        validateOnMount: true,
        validate: validateFormikWithZodSchema(formFieldsPlaygroundSchema),
        onSubmit: setSubmittedValues,
    });

    return (
        <SandboxLayout>
            <ShowcasePreview
                alignment="start"
                width="full"
                summary={
                    submittedValues
                        ? "Submitted values"
                        : "Live values · fix the error column, then submit"
                }
                output={JSON.stringify(
                    submittedValues ?? formik.values,
                    showUndefined,
                    2,
                )}
            >
                <FormikProvider value={formik}>
                    <Form>
                        <VStack gap={24}>
                            <FormFieldsPlaygroundRows />
                            <FormFieldsPlaygroundPickerRows />
                            <FormFieldsPlaygroundContactRows />
                            <FormFieldsPlaygroundChoiceRows />
                            <FormFieldsPlaygroundLabellessRows />
                            <HStack gap={12}>
                                <Button type="submit" variant="primary">
                                    Submit
                                </Button>
                                <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={() => {
                                        formik.resetForm();
                                        setSubmittedValues(undefined);
                                    }}
                                >
                                    Reset
                                </Button>
                            </HStack>
                        </VStack>
                    </Form>
                </FormikProvider>
            </ShowcasePreview>
        </SandboxLayout>
    );
};
